import type { PaginationState } from '@tanstack/react-table'
import { StateCreator, createStore } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import {
  type ConfirmationStatusFilter,
  DEFAULT_CONFIRMATION_STATUS_FILTERS,
} from '@/components/library-v2/lib'

import { IS_PROD_LIKE_ENVIRONMENT } from '@/constants/env'

import type { UUID } from '@/types/Id'

// region store types

export interface CompaniesStoreFilterState {
  industries: string[]
  confirmationStatuses: ConfirmationStatusFilter[]
  relationships: string[]
  relationshipStatuses: string[]
}

export interface CompaniesStoreState {
  filters: CompaniesStoreFilterState
  setFilters: (filters: Partial<CompaniesStoreFilterState>) => void
  addRelationshipStatusFilter: (status: string) => void
  resetFilters: () => void

  searchQuery: string
  setSearchQuery: (query: string) => void

  selectedCompanyIds: UUID[]
  setSelectedCompanyIds: (ids: UUID[]) => void

  lastSelectedRowId: string | null
  setLastSelectedRowId: (id: string | null) => void

  pagination: PaginationState
  setPagination: (pagination: PaginationState) => void

  deleteDialogOpen: boolean
  setDeleteDialogOpen: (open: boolean) => void

  mergeDialogOpen: boolean
  setMergeDialogOpen: (open: boolean) => void

  confirmDialogOpen: boolean
  setConfirmDialogOpen: (open: boolean) => void

  createDialogOpen: boolean
  setCreateDialogOpen: (open: boolean) => void

  importDialogOpen: boolean
  setImportDialogOpen: (open: boolean) => void
}

const makeInitStore = (): StateCreator<CompaniesStoreState> => (set, get) => ({
  filters: {
    industries: [],
    confirmationStatuses: [...DEFAULT_CONFIRMATION_STATUS_FILTERS],
    relationships: [],
    relationshipStatuses: [],
  },
  setFilters: (filters) => {
    const existingFilters = get().filters
    const newFilters = { ...existingFilters, ...filters }
    set({ filters: newFilters })
  },
  addRelationshipStatusFilter: (status) => {
    const existing = get().filters.relationshipStatuses
    if (!existing.includes(status)) {
      set({
        filters: {
          ...get().filters,
          relationshipStatuses: [...existing, status],
        },
      })
    }
  },
  resetFilters: () =>
    set({
      filters: {
        industries: [],
        confirmationStatuses: [...DEFAULT_CONFIRMATION_STATUS_FILTERS],
        relationships: [],
        relationshipStatuses: [],
      },
    }),

  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  selectedCompanyIds: [],
  setSelectedCompanyIds: (ids) => set({ selectedCompanyIds: ids }),

  lastSelectedRowId: null,
  setLastSelectedRowId: (id) => set({ lastSelectedRowId: id }),

  pagination: { pageIndex: 0, pageSize: 50 },
  setPagination: (pagination) => set({ pagination }),

  deleteDialogOpen: false,
  setDeleteDialogOpen: (open) => set({ deleteDialogOpen: open }),

  mergeDialogOpen: false,
  setMergeDialogOpen: (open) => set({ mergeDialogOpen: open }),

  confirmDialogOpen: false,
  setConfirmDialogOpen: (open) => set({ confirmDialogOpen: open }),

  createDialogOpen: false,
  setCreateDialogOpen: (open) => set({ createDialogOpen: open }),

  importDialogOpen: false,
  setImportDialogOpen: (open) => set({ importDialogOpen: open }),
})

// end region store types

// region store

export type CompaniesStore = ReturnType<typeof createCompaniesStore>

export const createCompaniesStore = () => {
  if (IS_PROD_LIKE_ENVIRONMENT) {
    return createStore<CompaniesStoreState, [['zustand/immer', never]]>(
      immer<CompaniesStoreState>(makeInitStore()),
    )
  }
  return createStore<
    CompaniesStoreState,
    [['zustand/devtools', never], ['zustand/immer', never]]
  >(devtools(immer<CompaniesStoreState>(makeInitStore())))
}

// end region store
